import React, { memo, useState } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Button, message, Select, Typography } from 'antd';
import { PlayCircleFilled, VideoCameraOutlined, FontSizeOutlined, PictureOutlined, LoadingOutlined, DeleteOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';
import api from '../../../api/client';
import { useParams } from 'react-router-dom';
import { useGeneration } from '../../../context/GenerationContext';

const { Text } = Typography;

const VideoNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { getNodes, getEdges, setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Node State
    const duration = data.duration || "5";

    const updateData = (key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        [key]: value
                    }
                };
            }
            return node;
        }));

        if (data.onSave) {
            data.onSave(id, { [key]: value });
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const appEdges = getEdges();
            const appNodes = getNodes();

            // 1. Get Prompt (Left Top)
            const promptEdge = appEdges.find(e => e.target === id && e.targetHandle === 'left-top');
            let prompt = data.prompt || "";
            if (promptEdge) {
                const sourceNode = appNodes.find(n => n.id === promptEdge.source);
                if (sourceNode?.data?.text) {
                    prompt = sourceNode.data.text;
                }
            }

            if (!prompt) {
                message.warning('Please provide a prompt via the text input or connection.');
                setGenerating(false);
                return;
            }

            // 2. Get Start Image (Left Middle)
            const startImageEdge = appEdges.find(e => e.target === id && e.targetHandle === 'left-middle');
            let startImageUrl = null;
            if (startImageEdge) {
                const sourceNode = appNodes.find(n => n.id === startImageEdge.source);
                if (sourceNode?.data?.src) {
                    startImageUrl = sourceNode.data.src;
                }
            }

            if (!startImageUrl) {
                message.warning('Start image is required. Connect an image node to the middle handle.');
                setGenerating(false);
                return;
            }

            // 3. Get End Image (Left Bottom) - Optional
            const endImageEdge = appEdges.find(e => e.target === id && e.targetHandle === 'left-bottom');
            let endImageUrl = null;
            if (endImageEdge) {
                const sourceNode = appNodes.find(n => n.id === endImageEdge.source);
                if (sourceNode?.data?.src) {
                    endImageUrl = sourceNode.data.src;
                }
            }

            const payload = {
                prompt,
                image_url: startImageUrl,
                duration,
                ...(endImageUrl && { tail_image_url: endImageUrl })
            };

            const response = await api.post('/fal/kling/video', payload);
            const { request_id } = response.data;
            const modelId = 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video';

            // setGenerating(false); // REMOVED

            trackGeneration(request_id, modelId, {
                prompt: 'Kling Video',
                type: 'Video',
                canvas_id: canvasId,
                node_id: id,
                promptProp: prompt
            }, {
                onComplete: async (result) => {
                    if (result && result.video && result.video.url) {
                        let videoUrl = result.video.url;

                        // Saving is now handled by the backend via FalResultProcessor


                        updateData('src', videoUrl);
                        message.success('Video generated successfully!');
                    } else {
                        message.error('Failed to generate video.');
                    }
                    setGenerating(false); // Stop glow
                },
                onFail: (err) => {
                    console.error("Video Generation failed via queue:", err);
                    setGenerating(false);
                }
            });

        } catch (error) {
            console.error(error);
            message.error('Generation failed: ' + (error.response?.data?.error || error.message));
            setGenerating(false); // Stop glow if submission fails
        }
    };

    const clearVideo = (e) => {
        e.stopPropagation();
        updateData('src', null);
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={600}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Kling Video'}
                iconType={data.iconType || 'video-camera'}
                color={data.nodeColor || TYPE_COLORS.video}
                onTitleChange={onTitleChange}
            />

            {/* Input Handles */}
            <Handle
                type="target"
                position={Position.Left}
                id="left-top"
                style={{
                    ...getHandleStyle(TYPE_COLORS.text, 'left', handlesVisible),
                    top: 40
                }}
            />

            <Handle
                type="target"
                position={Position.Left}
                id="left-middle"
                style={{
                    ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible),
                    top: 100
                }}
            />

            <Handle
                type="target"
                position={Position.Left}
                id="left-bottom"
                style={{
                    ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible),
                    top: 160
                }}
            />


            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                style={getHandleStyle(TYPE_COLORS.video, 'right', handlesVisible)}
            />

            {/* Generate Button */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 48 }} /> : <PlayCircleFilled style={{ fontSize: 48 }} />}
                onClick={handleGenerate}
                style={{
                    position: 'absolute',
                    right: -130, // Adjusted based on MediaNode pattern
                    bottom: 0,
                    width: 100, // Slightly smaller than MediaNode
                    height: 100,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '8px solid #161b22',
                    background: '#722ed1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', background: '#000' }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: generating ? '1px solid #722ed1' : '1px solid #30363d',
                    boxShadow: generating
                        ? '0 0 15px #722ed1, 0 0 30px rgba(82, 196, 26, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.5)',
                    overflow: 'hidden',
                    borderRadius: 8,
                    animation: generating ? 'pulse-glow 2s infinite' : 'none'
                }}
            >
                <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {data.src ? (
                        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                            <video
                                controls
                                src={data.src}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                            <div
                                className="nodrag delete-btn"
                                onClick={clearVideo}
                                style={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    background: 'rgba(0,0,0,0.6)',
                                    borderRadius: '50%',
                                    width: 32,
                                    height: 32,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer',
                                    zIndex: 20
                                }}
                            >
                                <DeleteOutlined style={{ color: '#fff', fontSize: 16 }} />
                            </div>
                        </div>
                    ) : (
                        <div style={{ color: '#8b949e', textAlign: 'center' }}>
                            <VideoCameraOutlined style={{ fontSize: 48, marginBottom: 10 }} />
                            <div>Connect inputs and press Play</div>
                            <div style={{ fontSize: 12, marginTop: 5, color: '#555' }}>
                                Top: Prompt<br />
                                Middle: Start Frame (Req)<br />
                                Bottom: End Frame (Opt)
                            </div>
                        </div>
                    )}
                </div>

                {/* Controls Bar */}
                <div style={{
                    padding: '8px 12px',
                    background: '#161b22',
                    borderTop: '1px solid #30363d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 50,
                    gap: 8
                }}>
                    <Text style={{ color: '#c9d1d9', fontSize: 12 }}>Duration</Text>
                    <Select
                        value={duration}
                        onChange={(v) => updateData('duration', v)}
                        style={{ width: 80 }}
                        className="pill-select nodrag"
                        options={[
                            { value: '5', label: '5s' },
                            { value: '10', label: '10s' }
                        ]}
                    />
                </div>
            </Card>
            <style>{`
                .pill-select .ant-select-selector {
                    background: #30363d !important;
                    border-radius: 8px !important;
                    color: #fff !important;
                    height: 32px !important;
                    display: flex !important;
                    align-items: center !important;
                    font-size: 13px !important;
                    border: none !important;
                }
                .pill-select .ant-select-arrow {
                    color: #8b949e;
                }
                 @keyframes pulse-glow {
                    0% {
                        box-shadow: 0 0 15px #722ed1, 0 0 30px rgba(82, 196, 26, 0.4);
                        border-color: #722ed1;
                    }
                    50% {
                        box-shadow: 0 0 25px #9254de, 0 0 50px rgba(82, 196, 26, 0.6);
                        border-color: #9254de;
                    }
                    100% {
                        box-shadow: 0 0 15px #722ed1, 0 0 30px rgba(82, 196, 26, 0.4);
                        border-color: #722ed1;
                    }
                }
            `}</style>
        </div>
    );
};

export default memo(VideoNode);
